import { v4 as uuidv4 } from "uuid";
import { DocumentUploadStrategy } from "./DocumentUploadStrategy";
import { UserContext } from "../utils/UserContext";
import { DocumentLinkManager } from "../managers/DocumentLinkManager";
import { DocumentVersionManager } from "../managers/DocumentVersionManager";
import { DomainResolver } from "../managers/DomainResolver";

export class LinkedDocumentUploadStrategy implements DocumentUploadStrategy {
  constructor(
    private client: any,
    private context: {
      linkedEntityId: string;
      linkedEntityType: "APPLICATION" | "GENUINE_STUDENT";
      allowMultiple?: boolean;
      institutionId?: string;
    },
    private documentLinkManager: DocumentLinkManager,
    private documentVersionManager: DocumentVersionManager,
    private domainResolver: DomainResolver,
  ) {}

  async execute(
    userContext: UserContext,
    s3Key: string,
    fileName: string,
    documentType: string,
    contentHash: string,
  ): Promise<void> {
    try {
      const existingLink = await this.documentLinkManager.findExistingLink(
        userContext,
        documentType,
        this.context,
      );

      if (existingLink && !this.context.allowMultiple) {
        await this.handleVersioning(
          userContext,
          s3Key,
          fileName,
          documentType,
          existingLink,
          contentHash,
        );
      } else {
        await this.handleNewDocument(
          userContext,
          s3Key,
          fileName,
          documentType,
          contentHash,
        );
      }
    } catch (error) {
      console.error("Execution failed.");
      throw error;
    }
  }

  private async handleVersioning(
    userContext: UserContext,
    s3Key: string,
    fileName: string,
    documentType: string,
    existingLink: any,
    contentHash: string,
  ): Promise<void> {
    const { data: existingDoc } = await this.client.models.Document.get(
      {
        userId: userContext.identityId,
        documentId: existingLink.documentId,
      },
      { authMode: "userPool", authToken: userContext.token },
    );

    if (!existingDoc) return;

    const documentData = this.buildDocumentData(
      userContext,
      s3Key,
      fileName,
      documentType,
      contentHash,
    );

    const newDocId = await this.documentVersionManager.createNewVersion(
      existingDoc.documentId,
      documentData,
      userContext,
    );

    await this.documentLinkManager.updateLink(
      existingLink.id,
      newDocId,
      fileName,
      s3Key,
      documentType,
      userContext,
    );
  }

  private async handleNewDocument(
    userContext: UserContext,
    s3Key: string,
    fileName: string,
    documentType: string,
    contentHash: string,
  ): Promise<void> {
    const documentData = this.buildDocumentData(
      userContext,
      s3Key,
      fileName,
      documentType,
      contentHash,
    );

    const documentId = await this.documentVersionManager.createInitialVersion(
      documentData,
      userContext,
    );

    const domain = await this.domainResolver.resolveDomain(userContext, {
      ...this.context,
      institutionId: this.context.institutionId,
    });

    const applicantId = await this.getApplicantId(userContext);

    await this.documentLinkManager.createLink(
      documentId,
      documentType,
      fileName,
      s3Key,
      domain,
      applicantId,
      this.context,
      userContext,
    );
  }

  private async getApplicantId(
    userContext: UserContext,
  ): Promise<string | null> {
    if (this.context.linkedEntityType === "APPLICATION") {
      try {
        const { data: application } = await this.client.models.Application.get(
          {
            institutionId: this.context.institutionId ?? userContext.userDomain,
            applicationId: this.context.linkedEntityId,
          },
          { authMode: "userPool", authToken: userContext.token },
        );
        return application?.owner || null;
      } catch (error) {
        console.error("Error fetching applicantId:", error);
        return null;
      }
    }
    return null;
  }

  private buildDocumentData(
    userContext: UserContext,
    s3Key: string,
    fileName: string,
    documentType: string,
    contentHash: string,
  ) {
    const documentId = uuidv4();

    return {
      documentId,
      userId: userContext.identityId,
      ownerType: userContext.userType,
      documentType,
      fileKey: s3Key,
      fileName,
      contentHash,
      status: "ACTIVE",
      uploadedAt: new Date().toISOString(),
    };
  }
}
